type FlowyHighlightTone = 'recommendation' | 'contrast' | 'evidence';

type HighlightTargetOptions = {
	target: HTMLElement;
	label?: string;
	reason?: string | null;
	tone?: FlowyHighlightTone;
	scroll?: boolean;
	ttlMs?: number;
};

type HighlightResultOptions = Omit<HighlightTargetOptions, 'target'> & {
	panelSelector: string;
	resultId: string;
};

type ActiveHighlight = {
	target: HTMLElement;
	callout: HTMLElement | null;
	cleanup: () => void;
};

let activeHighlight: ActiveHighlight | null = null;

const DEFAULT_TTL_MS = 14000;

const findResultElement = (panelSelector: string, resultId: string): HTMLElement | null => {
	const panel = document.querySelector(panelSelector);
	if (!panel) return null;
	const candidates = Array.from(panel.querySelectorAll<HTMLElement>('[data-result-id]'));
	return candidates.find((candidate) => candidate.dataset.resultId === resultId) || null;
};

const removeToneClasses = (target: HTMLElement) => {
	target.classList.remove(
		'flowy-highlight-target--recommendation',
		'flowy-highlight-target--contrast',
		'flowy-highlight-target--evidence'
	);
};

export const clearFlowyHighlight = () => {
	if (!activeHighlight) return;
	activeHighlight.cleanup();
	activeHighlight.target.classList.remove('flowy-highlight-target');
	removeToneClasses(activeHighlight.target);
	activeHighlight.callout?.remove();
	activeHighlight = null;
};

const positionCallout = (target: HTMLElement, callout: HTMLElement) => {
	const targetRect = target.getBoundingClientRect();
	const calloutRect = callout.getBoundingClientRect();
	const gutter = 10;
	const viewportPadding = 10;
	const preferredLeft = targetRect.left - calloutRect.width - gutter;
	const fallbackLeft = targetRect.right + gutter;
	const left =
		preferredLeft >= viewportPadding
			? preferredLeft
			: Math.min(fallbackLeft, window.innerWidth - calloutRect.width - viewportPadding);
	const top = Math.min(
		Math.max(targetRect.top + 8, viewportPadding),
		window.innerHeight - calloutRect.height - viewportPadding
	);

	callout.style.left = `${Math.max(viewportPadding, left)}px`;
	callout.style.top = `${Math.max(viewportPadding, top)}px`;
};

const createCallout = ({
	label,
	reason,
	tone,
	onClose,
}: {
	label: string;
	reason?: string | null;
	tone: FlowyHighlightTone;
	onClose: () => void;
}): HTMLElement => {
	const callout = document.createElement('div');
	callout.className = `flowy-highlight-callout flowy-highlight-callout--${tone}`;
	callout.setAttribute('role', 'note');

	const header = document.createElement('div');
	header.className = 'flowy-highlight-callout__header';
	header.innerText = label;
	callout.append(header);

	if (reason) {
		const body = document.createElement('div');
		body.className = 'flowy-highlight-callout__body';
		body.innerText = reason;
		callout.append(body);
	}

	const closeButton = document.createElement('button');
	closeButton.type = 'button';
	closeButton.className = 'flowy-highlight-callout__close';
	closeButton.setAttribute('aria-label', 'Dismiss Flowy highlight');
	closeButton.innerText = 'x';
	closeButton.addEventListener('click', onClose);
	callout.append(closeButton);

	document.body.append(callout);
	return callout;
};

export const highlightFlowyTarget = ({
	target,
	label = 'Flowy pick',
	reason = null,
	tone = 'recommendation',
	scroll = true,
	ttlMs = DEFAULT_TTL_MS,
}: HighlightTargetOptions) => {
	clearFlowyHighlight();

	target.classList.add('flowy-highlight-target', `flowy-highlight-target--${tone}`);
	target.setAttribute('aria-current', 'true');

	if (scroll) {
		target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
	}

	let timeout: number | undefined;
	let callout: HTMLElement | null = null;
	const cleanup = () => {
		if (timeout !== undefined) {
			window.clearTimeout(timeout);
		}
		window.removeEventListener('resize', updatePosition);
		window.removeEventListener('scroll', updatePosition, true);
		target.removeAttribute('aria-current');
	};
	const close = () => {
		clearFlowyHighlight();
	};
	const updatePosition = () => {
		if (callout && document.body.contains(callout)) {
			positionCallout(target, callout);
		}
	};

	callout = createCallout({ label, reason, tone, onClose: close });
	activeHighlight = { target, callout, cleanup };

	window.addEventListener('resize', updatePosition);
	window.addEventListener('scroll', updatePosition, true);
	window.setTimeout(updatePosition, scroll ? 260 : 0);

	if (ttlMs > 0) {
		timeout = window.setTimeout(() => {
			clearFlowyHighlight();
		}, ttlMs);
	}
};

export const highlightFlowyResult = ({
	panelSelector,
	resultId,
	...options
}: HighlightResultOptions): boolean => {
	const target = findResultElement(panelSelector, resultId);
	if (!target) return false;
	highlightFlowyTarget({ target, ...options });
	return true;
};
